import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { injectGlobal } from 'styled-components'
import Cookie from '../components/utils/Cookie'
import ErrorBoundaryComponent from '../components/utils/ErrorBoundaryComponent'
import Main from '../components/containers/Main'
import GA from '../components/utils/GA'
import LeaderBoard from '../components/widgets/Advert/LeaderBoard'
import Header from '../components/containers/Header'
import Logo from '../components/widgets/Header/Logo'
import Search from '../components/widgets/Header/Search'
import EmailLink from '../components/widgets/Header/EmailLink'
import HotSearch from '../components/widgets/Header/HotSearch'
import Content from '../components/containers/Content'
import SashecAdvertPositionWrapper from '../components/widgets/Advert/SashecAdvertPositionWrapper'
import StickyHalfpage from '../components/widgets/Advert/StickyHalfpage'
import LeftColumn from '../components/containers/LeftColumn'
import RightColumn from '../components/containers/RightColumn'
import Bottom from '../components/containers/Bottom'
import Footer from '../components/widgets/Footer'
import FooterLinks from '../components/widgets/Footer/FooterLinks'
import FooterSections from '../components/widgets/Footer/FooterSections'
import ExternalScripts from '../components/widgets/ExternalScripts'
import containers from '../containers'
import { detectDevice, isValidDevice } from '../components/utils/Devices'
import { sashecQueuePush, detectSashecDevice } from '../components/utils/SashecHelpers'
import { DEVICES, GA_ENHANCED_ECOMMERCE, GA_TRACKERS, LAYOUT_SECTIONS, WIDGETS } from '../constants'

injectGlobal`
  #homepage {
    overflow: hidden;
  }
`

class Layout extends Component {
  constructor (props) {
    super(props)
    this.state = {
      activeDynamicLayout: isValidDevice(props.dynamicLayoutCookie) ? props.dynamicLayoutCookie : detectDevice(),
      widgets: props.widgets,
      containers: containers(props.widgets)
    }

    this.dynamicWidgets = this.getDynamicWidgets()
    this.dynamicWidgetsLayout = this.getDynamicWidgetsLayout()
    this.updateDynamicLayoutCookie(props.dynamicLayoutCookie)

    if (props.clientSide) {
      const googleAnalytics = new GA(props.widgets.config.ga)

      const pagesTracker = googleAnalytics.getTracker(GA_TRACKERS.PAGEVIEW)
      pagesTracker.set({dimension2: this.getGADimensionCookie()})
      pagesTracker.pageview('/')

      if (props.widgets.config.ga.trackers.ecommerce) {
        const ecommerceTracker = googleAnalytics.getTracker(GA_TRACKERS.ECOMMERCE)
        ecommerceTracker.require(GA_ENHANCED_ECOMMERCE.ECOMMERCE_PLUGIN)
        this.state.googleAnalytics = ecommerceTracker
      }

      this.googleAnalytics = googleAnalytics
    }

    this.handleResize = this.handleResize.bind(this)
  }

  componentWillMount () {
    if (!this.props.clientSide) {
      return false
    }

    const {
      meta: {
        sashec: {
          site,
          template,
          area
        }
      }
    } = this.state.widgets.layout
    const sashecDevice = detectSashecDevice()
    const sashecPayload = [
      'group',
      {
        site,
        targets: {
          device: sashecDevice,
          template
        },
        area
      }
    ]
    sashecQueuePush(sashecPayload)
  }

  componentDidMount () {
    if (this.props.clientSide) {
      this.state.containers.inbox.loadInbox()
      window.addEventListener('resize', this.handleResize)
    }
  }

  componentWillUnmount () {
    if (this.props.clientSide) {
      window.removeEventListener('resize', this.handleResize)
    }
  }

  handleResize () {
    const currentDevice = detectDevice()
    if (this.state.activeDynamicLayout !== currentDevice) {
      this.updateDynamicLayoutCookie(currentDevice)
      this.setState({
        activeDynamicLayout: currentDevice
      })
    }
  }

  getGADimensionCookie () {
    const cookie = new Cookie()
    const gaDimensionCookieName = this.props.widgets.config.cookies.gaCustomDimension

    return cookie.isSet(gaDimensionCookieName) ? cookie.cookieJar[gaDimensionCookieName] : ''
  }

  updateDynamicLayoutCookie (layout) {
    if (typeof document !== 'undefined') {
      const cookieName = this.state.widgets.config.cookies.dynamicLayout
      const date = new Date()
      date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000)
      document.cookie = `${cookieName}=${layout}; expires=${date.toGMTString()}`
    }
  }

  getDynamicWidgets () {
    const {
      widgets,
      widgets: {
        config: {
          proxyOptions
        }
      },
      containers: {
        inbox
      }
    } = this.state

    return {
      [WIDGETS.COMMON.LEADERBOARD]: <LeaderBoard widget={widgets.leaderboard}/>,
      [WIDGETS.COMMON.STRIP]: <LeaderBoard widget={widgets.strip}/>,
      [WIDGETS.COMMON.LOGO]: <Logo widget={widgets.logo}/>,
      [WIDGETS.COMMON.SEARCH]: <Search widget={widgets.search} proxyOptions={proxyOptions}/>,
      [WIDGETS.COMMON.EMAIL_LINK]: <EmailLink widget={widgets.emaillink} container={inbox}/>,
      [WIDGETS.COMMON.HOT_SEARCH]: <HotSearch widget={widgets.hotsearch}/>,
      [WIDGETS.COMMON.RIGHT_BANNER]: <SashecAdvertPositionWrapper widget={widgets.rightbanner}/>,
      [WIDGETS.COMMON.HALF_PAGE]: <StickyHalfpage widget={widgets.halfpage}/>,
      [WIDGETS.COMMON.SMART_ADVERT_1]: <SashecAdvertPositionWrapper widget={widgets.advertsmart1}/>,
      [WIDGETS.COMMON.SMART_ADVERT_2]: <SashecAdvertPositionWrapper widget={widgets.advertsmart2}/>,
      [WIDGETS.COMMON.SMART_ADVERT_3]: <SashecAdvertPositionWrapper widget={widgets.advertsmart3}/>
    }
  }

  getDynamicWidgetsLayout () {
    return {
      [DEVICES.DESKTOP]: this.getDesktopWidgetsLayout(),
      [DEVICES.TABLET]: this.getTabletWidgetsLayout(),
      [DEVICES.MOBILE]: this.getMobileWidgetsLayout()
    }
  }

  getDesktopWidgetsLayout () {
    return {}
  }

  getTabletWidgetsLayout () {
    return {}
  }

  getMobileWidgetsLayout () {
    return {}
  }

  renderDynamicWidgetsLayout (position) {
    const activeDynamicLayout = this.state.activeDynamicLayout
    return this.dynamicWidgetsLayout[activeDynamicLayout][position].map(widgetId => {
      if (typeof this.state.widgets[widgetId.toLowerCase()] === 'undefined') {
        return null
      }
      const widgetComponent = this.dynamicWidgets[widgetId]
      const widgetState = Object.assign({}, this.state.widgets[widgetId.toLowerCase()], {
        device: activeDynamicLayout
      })

      return (
        <ErrorBoundaryComponent key={widgetId}>
          {
            React.cloneElement(
              widgetComponent,
              Object.assign({}, { widget: widgetState }, this.state)
            )
          }
        </ErrorBoundaryComponent>
      )
    })
  }

  renderTop () {
    return this.renderDynamicWidgetsLayout(LAYOUT_SECTIONS.TOP)
  }

  renderHeader () {
    return this.renderDynamicWidgetsLayout(LAYOUT_SECTIONS.HEADER)
  }

  renderOverContent () {
    return this.renderDynamicWidgetsLayout(LAYOUT_SECTIONS.OVER_CONTENT)
  }

  renderLeftColumn () {
    return this.renderDynamicWidgetsLayout(LAYOUT_SECTIONS.LEFT_COLUMN)
  }

  renderRightColumn () {
    return this.renderDynamicWidgetsLayout(LAYOUT_SECTIONS.RIGHT_COLUMN)
  }

  renderBottom () {
    return this.renderDynamicWidgetsLayout(LAYOUT_SECTIONS.BOTTOM)
  }

  renderOutsideContent () {
    return this.renderDynamicWidgetsLayout(LAYOUT_SECTIONS.OUTSIDE_CONTENT)
  }

  render () {
    const {
      props: {
        layout
      },
      state: {
        widgets
      }
    } = this

    return (
      <Fragment>
        { this.renderTop() }
        <Main layout={layout}>
          <Header>
            { this.renderHeader() }
          </Header>
          { this.renderOverContent() }
          <Content>
            <LeftColumn>
              { this.renderLeftColumn() }
            </LeftColumn>
            <RightColumn>
              { this.renderRightColumn() }
            </RightColumn>
          </Content>
          <Bottom>
            { this.renderBottom() }
          </Bottom>
          <Footer>
            <FooterSections widgets={widgets}/>
            <FooterLinks widget={widgets.footerlinks}/>
          </Footer>
          { this.renderOutsideContent() }
          <ExternalScripts widget={widgets.bottomscripts}/>
        </Main>
      </Fragment>
    )
  }
}

Layout.propTypes = {
  widgets: PropTypes.object.isRequired,
  layout: PropTypes.string.isRequired,
  clientSide: PropTypes.bool,
  dynamicLayoutCookie: PropTypes.string
}

Layout.defaultProps = {
  dynamicLayoutCookie: detectDevice()
}

export default Layout
